import { Loading, Panel } from "@appkit4/react-components";
import "@glideapps/glide-data-grid/dist/index.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CompanyPanel from "../../components/CompanyPanel";
import toast from "../../components/DismissibleToast";
import { Button, Input } from "../../components/ReadonlyAwareInputs";
import { AuthContext } from "../../services/AuthProvider";
import { getMetrics } from "../../services/calculations/EntityCalculations";
import { addCharge } from "../../services/Charging.service";
import { get_company_data } from "../../services/dredger/dredger.service";
import {
  canProgressCalculation,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getPoaIndex,
  getSelectedPeriod,
  setGroupData,
} from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import {
  checkChangedMetricsList,
  getAdjCapsInclusionMetrics,
  getAdjustedRow,
  getEntityInputCaps,
  initAdjustedCaps,
} from "../../utils/adjustedCapsUtil";
import { cap_adjusted_values } from "../../utils/CapCurrencyValues";
import { generateAdjCapId, generateCAPId } from "../../utils/capProcessor";
import { formatDate, processDateJS } from "../../utils/dateProcessor";
import { getPriorPoas } from "../../utils/PeriodUtils";
import EntityInputDataEditor from "./components/EntityInputDataEditor";
import OneSourceRefreshUpdatesPanel from "./components/OneSourceRefreshUpdatesPanel";
import "./entityInput.scss";
import MADElectionModal from "./MADElectionModal";
import {
  capDatesMatchPoADates,
  capDatesWithinRange,
  clearMADElectionDatesOnDisable,
  isActualEndDateEntryInvalid,
  isActualStartDateEntryInvalid,
  populateMADElectionDatesOnEnable,
  validateActualDatesForCap,
} from "./util/entityInputUtils";
import { buildOneSourceRefreshSummary } from "./util/onesourceRefreshSummaryUtils";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

const REFRESH_CALCULATED_METRIC_IDS = ["net_tax_interest_totals", "tax_ebitda"];

const valuesAreDifferent = (previousValue, nextValue) => {
  if (Number.isNaN(previousValue) && Number.isNaN(nextValue)) {
    return false;
  }

  return previousValue !== nextValue;
};

const EntityInput = () => {
  const [isLoading, setLoading] = useState(true);
  const selectedPeriod = getSelectedPeriod();
  const {
    period_start,
    period_end,
    hasPoaSpecificWbsCode,
    wbsCode: poaWbsCode,
  } = selectedPeriod ?? {};
  const poaIndex = getPoaIndex();
  const auth = useContext(AuthContext);
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);
  const tenantId = useSelector((state) => state.usageInfo.tenantID);
  const [data, set_data] = useState([]);
  const [
    last_refreshed_from_onesource_dates,
    set_last_refreshed_from_onesource_dates,
  ] = useState([]);
  const [is_refreshing, set_is_refreshing] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isAPsError, setIsAPsError] = useState(false);

  const [wbsCode, setWbsCode] = useState("");
  const [wbsCodeError, setWbsCodeError] = useState(false);

  const [madElectionModalVisible, setMadElectionModalVisible] = useState(false);
  const [isMadElectionApplied, setIsMadElectionApplied] = useState(
    selectedPeriod?.is_mad_election_applied ?? false,
  );

  const [capProps, setCapProps] = useState([]);
  const [changedMetricHighlights, setChangedMetricHighlights] = useState({});
  const [refreshSummary, setRefreshSummary] = useState({
    hasRunRefresh: false,
    changedCaps: [],
  });

  const navigate = useNavigate();

  const metrics = useMemo(() => getMetrics(), []);

  useEffect(() => {
    // If the period of account has not been set properly, let's return now.
    if (!selectedPeriod || !period_start || !period_end) {
      set_data([]);
      setLoading(false);
      return;
    }

    const saved_data = getGroupData();
    if (!saved_data) {
      toast.error("Failed to fetch the Group being edited");
      return;
    }

    // Let's find all the company accounting periods that fall within this given Group PoA
    const columns = getEntityInputCaps();
    if (columns?.length <= 0) {
      toast.error(
        "There are no company accounting periods within this Group PoA",
      );
      navigate("/app/companyap");
    }

    const lastRefreshedFromOnesourceDates = [];
    const props = [];

    columns.forEach((cap) => {
      lastRefreshedFromOnesourceDates.push(
        cap?.onesource_last_refresh_date_time || "",
      );

      props.push({
        company: saved_data?.companies?.find(
          (comp) => comp.company_ID === cap?.company_ID,
        ),
        // isCapDatesMatchPoaDates: capDatesMatchPoADates(cap, selectedPeriod),
        // isActualStartDateEntryInvalid: isActualStartDateEntryInvalid(
        //   cap,
        //   columns,
        // ),
        // isActualEndDateEntryInvalid: isActualEndDateEntryInvalid(cap),
        isCapUsedInPriorPoas:
          getPriorPoas(cap, selectedPeriod, saved_data)?.length > 0,
        lastRefreshedFromOnesourceDates:
          cap?.onesource_last_refresh_date_time || "",
      });
    });

    set_last_refreshed_from_onesource_dates(lastRefreshedFromOnesourceDates);
    setCapProps(props);

    set_data(columns);

    setWbsCode(
      (hasPoaSpecificWbsCode ? poaWbsCode : saved_data?.wbsCode) ?? "",
    );

    setLoading(false);
  }, []);

  const validateAllCapsActualDates = (data) => {
    for (const cap of data) {
      const isActualStartDateInvalid = isActualStartDateEntryInvalid(cap, data);
      const isActualEndDateInvalid = isActualEndDateEntryInvalid(cap);

      if (isActualStartDateInvalid || isActualEndDateInvalid) return false;
    }

    return true;
  };

  useEffect(() => {
    const hasPositiveValue = data?.some(
      (dataItem) => dataItem?.net_dtr_adjustment_gbp > 0,
    );
    setIsError(hasPositiveValue);
  }, [data]);

  const getChangedMetricHighlights = (
    previousCaps,
    changedMetrics,
    refreshedCapsByKey,
  ) => {
    const highlights = {};

    previousCaps?.forEach((cap, index) => {
      const capKey = generateCAPId(cap);
      const refreshedCap = refreshedCapsByKey?.get(capKey);

      if (!refreshedCap) {
        return;
      }

      const changedMetricIds = Object.keys(changedMetrics?.[index] || {});

      if (changedMetricIds.length === 0) {
        return;
      }

      const capId = generateCAPId(refreshedCap);

      if (!capId) {
        return;
      }

      highlights[capId] = changedMetricIds.reduce((acc, metricId) => {
        acc[metricId] = true;
        return acc;
      }, {});
    });

    return highlights;
  };

  const augmentChangedMetricsWithCalculatedValues = (
    previousCaps,
    changedMetrics,
    refreshedCapsByKey,
  ) => {
    const changedMetricsWithCalculatedValues = (changedMetrics ?? []).map(
      (capChangedMetrics) => ({ ...(capChangedMetrics ?? {}) }),
    );

    const calculatedMetricsById = new Map(
      (metrics ?? [])
        .filter(
          (metric) =>
            REFRESH_CALCULATED_METRIC_IDS.includes(metric?.metric_id) &&
            typeof metric?.calculation === "function",
        )
        .map((metric) => [metric.metric_id, metric]),
    );

    (previousCaps ?? []).forEach((previousCap, index) => {
      const capKey = generateCAPId(previousCap);
      const refreshedCap = refreshedCapsByKey?.get(capKey);

      if (!refreshedCap) {
        return;
      }

      const capChangedMetrics = {
        ...(changedMetricsWithCalculatedValues[index] ?? {}),
      };

      REFRESH_CALCULATED_METRIC_IDS.forEach((metricId) => {
        const metric = calculatedMetricsById.get(metricId);

        if (!metric?.calculation) {
          return;
        }

        const previousValue = metric.calculation({ ...previousCap });
        const refreshedValue = metric.calculation({ ...refreshedCap });

        if (valuesAreDifferent(previousValue, refreshedValue)) {
          capChangedMetrics[metricId] = {
            from: previousValue,
            to: refreshedValue,
          };
        }
      });

      changedMetricsWithCalculatedValues[index] = capChangedMetrics;
    });

    return changedMetricsWithCalculatedValues;
  };

  const clearChangedMetricHighlightForCell = (capId, metricId) => {
    if (!capId || !metricId) {
      return;
    }

    setChangedMetricHighlights((previousHighlights) => {
      const currentCapHighlights = previousHighlights?.[capId];

      if (!currentCapHighlights?.[metricId]) {
        return previousHighlights;
      }

      const nextCapHighlights = { ...currentCapHighlights };
      delete nextCapHighlights[metricId];

      const nextHighlights = { ...previousHighlights };

      if (Object.keys(nextCapHighlights).length === 0) {
        delete nextHighlights[capId];
      } else {
        nextHighlights[capId] = nextCapHighlights;
      }

      return nextHighlights;
    });
  };

  const updateOneSourceLastRefreshedDates = (
    failed_refresh_ids,
    updatedData,
  ) => {
    const refresh_date_time = dayjs().utc().format("YYYY-MM-DD HH:mm [UTC]");
    const last_refresh_dates = [...last_refreshed_from_onesource_dates];

    updatedData?.forEach((col, index) => {
      if (
        col?.onesource_sync &&
        !failed_refresh_ids?.includes(col?.company_ID)
      ) {
        last_refresh_dates[index] = refresh_date_time;
      }
    });

    set_last_refreshed_from_onesource_dates(last_refresh_dates);
    setCapProps((prevState) => {
      return prevState.map((prop, index) => ({
        ...prop,
        lastRefreshedFromOnesourceDates: last_refresh_dates[index],
      }));
    });
    set_data(
      updatedData.map((cap, index) => ({
        ...cap,
        onesource_last_refresh_date_time: last_refresh_dates[index],
      })),
    );
  };

  const refresh_one_source = async () => {
    if (wbsCodeError || !wbsCode) {
      toast.error("Please enter a valid WBS code");
      return;
    }

    const onesource_caps =
      data?.filter((cap) => cap?.onesource_sync && cap?.from_onesource) ?? [];
    if (onesource_caps?.length === 0) {
      toast.error(
        "No company accounting periods have been selected to refresh from OneSource",
      );
      return;
    }

    const userEmail = auth?.getEmail();

    setChangedMetricHighlights({});
    setRefreshSummary({
      hasRunRefresh: false,
      changedCaps: [],
    });
    set_is_refreshing(true);

    const chargePromise = addCharge(
      tenantId,
      "variable",
      onesource_caps,
      wbsCode,
      selectedPeriod,
      isClientFacing,
      null,
    );

    let companies_failed_refresh_ids = [];
    const refreshPromise = get_company_data(
      onesource_caps,
      period_start,
      period_end,
      userEmail,
    ).then(({ company_accounting_periods }) => {
      const changedMetrics = checkChangedMetricsList(
        onesource_caps,
        company_accounting_periods,
      );
      const lookup_map = new Map(
        onesource_caps?.map((item) => [
          generateCAPId(item),
          { item, index: data?.indexOf(item) },
        ]),
      );

      const companies_failed_refresh = [];
      const updatedData = [...data];
      const refreshedCapsByKey = new Map();

      onesource_caps?.forEach((old_cap) => {
        const key = generateCAPId(old_cap);
        const new_cap = company_accounting_periods?.find(
          (cap) => generateCAPId(cap) === key,
        );

        if (!new_cap) {
          companies_failed_refresh?.push(old_cap);
          return;
        }

        const { index: dataIndex } = lookup_map?.get(key);

        if (
          processDateJS(old_cap.start_date)?.isSame(
            processDateJS(new_cap.start_date),
            "day",
          ) &&
          processDateJS(old_cap.end_date)?.isSame(
            processDateJS(new_cap.end_date),
            "day",
          )
        ) {
          const mergedCap = { ...old_cap, ...new_cap };
          updatedData[dataIndex] = mergedCap;
          refreshedCapsByKey.set(key, mergedCap);
        } else {
          companies_failed_refresh?.push(new_cap);
        }
      });

      const changedMetricsWithCalculatedValues =
        augmentChangedMetricsWithCalculatedValues(
          onesource_caps,
          changedMetrics,
          refreshedCapsByKey,
        );

      const newChangedMetricHighlights = getChangedMetricHighlights(
        onesource_caps,
        changedMetricsWithCalculatedValues,
        refreshedCapsByKey,
      );

      const oneSourceRefreshSummary = buildOneSourceRefreshSummary({
        previousCaps: onesource_caps,
        changedMetrics: changedMetricsWithCalculatedValues,
        refreshedCapsByKey,
        metrics,
      });

      let failedAPs = [];
      if (companies_failed_refresh?.length > 0) {
        failedAPs = companies_failed_refresh
          ?.map((cap) => {
            const startDate = formatDate(processDateJS(cap?.start_date));
            const endDate = formatDate(processDateJS(cap?.end_date));
            return `- ${cap?.company_name} (${startDate}-${endDate})`;
          })
          ?.join("\n");

        companies_failed_refresh_ids = companies_failed_refresh?.map(
          (ap) => ap.company_ID,
        );
      }

      updateOneSourceLastRefreshedDates(
        companies_failed_refresh_ids,
        updatedData,
      );

      if (failedAPs.length > 0) {
        console.error(`Failed to refresh the following APs:\n${failedAPs}`);
      }
      return { failedAPs, oneSourceRefreshSummary, newChangedMetricHighlights };
    });

    const combinedPromise = chargePromise
      .then(() => refreshPromise)
      .then(
        ({
          failedAPs,
          oneSourceRefreshSummary,
          newChangedMetricHighlights,
        }) => {
          setRefreshSummary({
            hasRunRefresh: true,
            ...oneSourceRefreshSummary,
          });
          setChangedMetricHighlights(newChangedMetricHighlights);
          set_is_refreshing(false);
          // failedAPs is now available here
          if (failedAPs?.length > 0) {
            throw `Failed to refresh the following APs:\n${failedAPs}`;
          }

          const changedCaps = oneSourceRefreshSummary?.changedCaps ?? [];
          const changedMetricCount = changedCaps.reduce(
            (total, cap) => total + (cap?.metrics?.length ?? 0),
            0,
          );

          return {
            changedCapCount: changedCaps.length,
            changedMetricCount,
          };
        },
      )
      .catch((err) => {
        set_is_refreshing(false);
        console.error("Failed to refresh from OneSource", err);
        throw "Failed to refresh from OneSource";
      });

    toast.promise(combinedPromise, {
      loading: "Refreshing...",
      error: (err) =>
        err?.message ||
        (typeof err === "string" ? err : "Failed to refresh from OneSource"),
      success: ({ changedCapCount, changedMetricCount }) => {
        if (changedMetricCount > 0) {
          return `OneSource refresh completed. ${changedMetricCount} metric changes were detected across ${changedCapCount} CAP${changedCapCount === 1 ? "" : "s"}. Review the details in the OneSource Refresh Updates section.`;
        }

        return "OneSource refresh completed. No tracked updates were detected.";
      },
    });
  };

  const SUBTOTAL_METRIC_IDS = [
    "ntlr_subtotal",
    "ntdc_subtotal",
    "tlr_subtotal",
    "tdc_subtotal",
    "ofa_subtotal",
    "cte_subtotal",
    "tax_ebitda",
    "bfd_subtotal",
    "s407_relief_dtr_adjustment_tax_rate_equal_uk",
  ];
  const save = () => {
    let savedData = getGroupData();
    if (!savedData) {
      throw "Something went wrong with fetching your cached data";
    }

    setIsPosting(true);

    if (auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly()) {
      setGroupData(savedData);
      setIsPosting(false);
      return;
    }

    const overlapOrGapExistMessage = checkForAPsOverlapOrGap();
    if (overlapOrGapExistMessage) {
      toast.error(overlapOrGapExistMessage);
      setIsPosting(false);
      return;
    }

    const subtotal_metrics = metrics?.filter((m) =>
      SUBTOTAL_METRIC_IDS?.includes(m?.metric_id),
    );

    const newAdjustedCaps = data?.map((cap, index) => {
      let cap_copy = {
        ...cap,
        onesource_last_refresh_date_time:
          last_refreshed_from_onesource_dates[index],
      };
      subtotal_metrics?.forEach((metric) => {
        let value = 0;
        if (metric?.calculation) {
          value = metric?.calculation(cap);
        } else {
          value = cap_copy[metric?.metric_id];
        }

        cap_copy[metric?.metric_id] = value;
      });

      return cap_copy;
    });

    const newAdjustedCapsMap = new Map(
      newAdjustedCaps?.map((cap) => [generateCAPId(cap), cap]) ?? [],
    );

    if (!hasPoaSpecificWbsCode && !wbsCodeError && !isClientFacing) {
      savedData.wbsCode = wbsCode;
    }

    savedData.company_accounting_periods =
      savedData?.company_accounting_periods?.map((existingCap) => {
        const cap_with_subtotals = newAdjustedCapsMap.get(
          generateCAPId(existingCap),
        );
        if (cap_with_subtotals) {
          return { ...existingCap, ...cap_with_subtotals };
        }
        return existingCap;
      });

    savedData.periods_of_account[poaIndex].adjusted_caps = initAdjustedCaps();

    const adjustedCapsMap = new Map(
      savedData.periods_of_account[poaIndex].adjusted_caps?.map((ac) => [
        generateCAPId(ac),
        ac,
      ]) ?? [],
    );
    const companyIdMap = new Map(
      savedData?.companies?.map((c) => [c?.company_ID, c]) ?? [],
    );

    const capAdjustedValuesSet = new Set(cap_adjusted_values);

    const firstAdjCapPerCompany = new Map();
    savedData.periods_of_account
      .flatMap((poa) => poa.adjusted_caps)
      .forEach((adjCap) => {
        const compId = adjCap?.company_ID;
        if (!firstAdjCapPerCompany.has(compId)) {
          firstAdjCapPerCompany.set(compId, adjCap);
        } else {
          const earliest = firstAdjCapPerCompany.get(compId);
          if (
            processDateJS(adjCap?.inclusion_period_start)?.isBefore(
              processDateJS(earliest?.inclusion_period_start),
            )
          ) {
            firstAdjCapPerCompany.set(compId, adjCap);
          }
        }
      });

    // CAPs / AdjCaps - O(n*m) processing but with pre-computed shared values
    let updatedNewCaps = [];

    // For each CAP
    for (let CAP of savedData.company_accounting_periods) {
      const correspondingAdjustedCap = adjustedCapsMap.get(generateCAPId(CAP));
      const company = companyIdMap.get(correspondingAdjustedCap?.company_ID);

      if (correspondingAdjustedCap) {
        const setProperty = (property, posCheck = null) => {
          if (CAP?.[property]) {
            correspondingAdjustedCap[property] =
              CAP[property] === true &&
              (!posCheck ||
                posCheck.includes(
                  correspondingAdjustedCap.AdjCapPosinCompanyAccountingPeriod,
                ));
          } else {
            correspondingAdjustedCap[property] = false;
          }
        };

        const firstCapForCompany = firstAdjCapPerCompany.get(
          correspondingAdjustedCap?.company_ID,
        );
        correspondingAdjustedCap.isFirstApForCompanyInGroup =
          generateAdjCapId(correspondingAdjustedCap) ===
          generateAdjCapId(firstCapForCompany);

        const prior_poas = getPriorPoas(
          correspondingAdjustedCap,
          selectedPeriod,
          savedData,
        );

        Object.entries(CAP)?.forEach(([key, entry]) => {
          if (
            capAdjustedValuesSet.has(key) &&
            !(correspondingAdjustedCap[key]?.manual_adjustment === true)
          ) {
            const db_value = correspondingAdjustedCap[key];
            const manual_adjustment = db_value?.manual_adjustment ?? false;
            const ap_total = CAP[key] ?? 0;

            const tmpRow = {
              metric_id: key,
              accounting_period_total: ap_total,
              manual_adjustment,
            };

            const adjustedRow = getAdjustedRow(
              CAP,
              correspondingAdjustedCap,
              selectedPeriod,
              tmpRow,
              {
                priorPoas: prior_poas,
              },
            );

            correspondingAdjustedCap[key] = {
              value: adjustedRow.current_cir_amount,
              manual_adjustment,
            };
          }
        });

        setProperty("trade_small_or_negligible");
        setProperty("trade_uncommercial_or_non_statutory");
        setProperty("investment_business_ceased");
        setProperty("cta10_change_in_ownership_rules");

        const carryOverMetricsToAdjustedCaps = [
          "election_s433_s434",
          "elections_amortised_cost_basis",
          "elections_s456_s457",
          "qualifying_infrastructure_election_date",
          "qualifying_infrastructure_first_ap_start",
          "type_banking_company",
          "type_charity_company",
          "type_cooperative_company",
          "type_insurance_company",
          "type_investment_manager",
          "type_reit_company",
          "type_ring_fence_company",
          "type_shipping_company",
          "fx_rate",
          "currency",
          "ct_rate_override",
          "corporation_tax_rate",
        ];

        for (let key of carryOverMetricsToAdjustedCaps) {
          correspondingAdjustedCap[key] = CAP[key];
        }

        correspondingAdjustedCap.non_consenting_company =
          CAP?.non_consenting_company ?? false;

        const apply_mean_accounting_date_election =
          CAP?.apply_mean_accounting_date_election;
        const actual_start_date = CAP?.actual_start_date;
        const actual_end_date = CAP?.actual_end_date;

        correspondingAdjustedCap.apply_mean_accounting_date_election =
          apply_mean_accounting_date_election;
        correspondingAdjustedCap.actual_start_date = actual_start_date;
        correspondingAdjustedCap.actual_end_date = actual_end_date;

        const inclusionMetrics = getAdjCapsInclusionMetrics(
          company,
          correspondingAdjustedCap,
          savedData,
          poaIndex,
        );

        if (inclusionMetrics) {
          const {
            inclusion_period_start,
            inclusion_period_end,
            inclusion_period_length,
            ap_length,
            inclusion_factor,
          } = inclusionMetrics;

          correspondingAdjustedCap.inclusion_period_start =
            inclusion_period_start;
          correspondingAdjustedCap.inclusion_period_end = inclusion_period_end;
          correspondingAdjustedCap.inclusion_period_length =
            inclusion_period_length;
          correspondingAdjustedCap.ap_length = ap_length;
          correspondingAdjustedCap.inclusion_factor = inclusion_factor;
        }

        updatedNewCaps.push(correspondingAdjustedCap);
      }
    }

    const canProgressCalc = canProgressCalculation();

    // save AdjCaps data
    const currentPoA = savedData.periods_of_account[poaIndex];
    savedData.periods_of_account[poaIndex] = {
      ...currentPoA,
      adjusted_caps: updatedNewCaps,
      is_mad_election_applied: isMadElectionApplied,
      ...canProgressCalc,
      ...(hasPoaSpecificWbsCode &&
        !wbsCodeError &&
        !isClientFacing && { wbsCode }),
    };

    /*savedData.periods_of_account[getPoaIndex()].adjusted_caps = savedData?.periods_of_account[getPoaIndex()]?.newAdjustedCaps?.map(adjCap => {
            const correspondingCompanyAP = savedData.company_accounting_periods.find(cap => generateCAPId(cap) === generateCAPId(adjCap));
            return applyInclusionFactorAdjustments(adjCap, correspondingCompanyAP);
        })*/

    const promise = sqlService?.postResponse(savedData);
    const onSuccess = () => {
      setGroupData(savedData);
      setIsPosting(false);
    };
    const onError = () => setIsPosting(false);
    return toast.save(promise, onSuccess, onError);
  };

  const applyMeanAccountingDateElection = (actualStartDate, actualEndDate) => {
    const modifiedCaps = data?.map((cap) => {
      const hasDatesMismatch = !capDatesMatchPoADates(cap, selectedPeriod);
      const datesWithinRange = capDatesWithinRange(
        cap,
        actualStartDate,
        actualEndDate,
      );

      if (hasDatesMismatch && datesWithinRange) {
        return {
          ...cap,
          apply_mean_accounting_date_election: true,
          actual_start_date: actualStartDate,
          actual_end_date: actualEndDate,
        };
      } else return cap;
    });

    setIsMadElectionApplied(true);
    set_data(modifiedCaps);
  };

  const getUniqueCompaniesInAPs = () => {
    const companyNames = data?.map((company) => company?.company_name);
    return [...new Set(companyNames)];
  };

  const getDatesPath = (aps) => {
    const datesPath = aps?.map((ap) => {
      if (ap?.apply_mean_accounting_date_election) {
        return {
          start: ap?.actual_start_date,
          end: ap?.actual_end_date,
        };
      } else
        return {
          start: ap?.start_date,
          end: ap?.end_date,
        };
    });

    return datesPath;
  };

  const isOneDayAfter = (firstDate, secondDate) => {
    return (
      processDateJS(firstDate)?.diff(processDateJS(secondDate), "day") === 1
    );
  };

  const checkForAPsOverlapOrGap = () => {
    const companyNames = getUniqueCompaniesInAPs();
    const apsWithOverlapOrGapDates = [];

    companyNames.forEach((company) => {
      const aps = data?.filter((ap) => ap?.company_name === company);
      const periods = getDatesPath(aps);

      for (let i = 1; i < periods.length; i++) {
        const periodStart = periods[i]?.start;
        const prevPeriodEnd = periods[i - 1]?.end;

        if (!isOneDayAfter(periodStart, prevPeriodEnd)) {
          apsWithOverlapOrGapDates.push(company);
          break;
        }
      }
    });

    if (apsWithOverlapOrGapDates.length > 0) {
      return `The following companies have accounting periods with gaps or overlaps. Please resolve this before progressing: ${apsWithOverlapOrGapDates.join(",")}`;
    }

    return false;
  };

  const updateCapProps = (data) => {
    const newCapProps = data.map((cap) => ({
      ...cap,
      isActualStartDateEntryInvalid: isActualStartDateEntryInvalid(cap, data),
      isActualEndDateEntryInvalid: isActualEndDateEntryInvalid(cap),
    }));
    setCapProps(newCapProps);
  };

  const onCellEdited = ([col, row], newValue, editedCap, metricId) => {
    const metric =
      metrics.find(
        (existingMetric) => existingMetric?.metric_id === metricId,
      ) || metrics[row];

    if (!metric) {
      return;
    }

    const capIndex = col - 1;
    const editedCapId = generateCAPId(editedCap);

    let value = newValue?.data;
    editedCap[metric.metric_id] = value;

    if (metric?.type === "date") {
      const dayjsDate = value ? processDateJS(value) : undefined;
      if (value && !dayjsDate?.isValid()) {
        toast.error("Date must be entered in dd/mm/yyyy format");
        return;
      }
      value = dayjsDate?.toDate();

      if (["actual_start_date", "actual_end_date"].includes(metric.metric_id)) {
        value = processDateJS(value);
        validateActualDatesForCap(editedCap, data);
      }
    }

    // Siva: TODO If the business team ask to implement column reordering on this page
    // We will need to update this to search by company name
    // This is because the order in the data variable will be different

    populateMADElectionDatesOnEnable(editedCap, metric, value, selectedPeriod);
    clearMADElectionDatesOnDisable(editedCap, metric, value);

    if (metric?.metric_id === "currency" && value === "GBP") {
      editedCap["fx_rate"] = 1;
    }

    editedCap[metric.metric_id] = value;

    const newData = [...data];
    newData[capIndex] = editedCap;

    if (
      [
        "apply_mean_accounting_date_election",
        "actual_start_date",
        "actual_end_date",
      ].includes(metric.metric_id)
    ) {
      const isValid = validateAllCapsActualDates(newData);
      setIsAPsError(!isValid);
      updateCapProps(newData);
    }

    set_data(newData);
    clearChangedMetricHighlightForCell(editedCapId, metric.metric_id);
  };

  if (isLoading) {
    <Loading
      loadingType="linear"
      indeterminate={true}
      compact={false}
      className="page-loader"
    />;
    return;
  }

  return (
    <>
      <CompanyPanel />
      <MADElectionModal
        isVisible={madElectionModalVisible}
        setIsVisible={setMadElectionModalVisible}
        onClick={applyMeanAccountingDateElection}
      />

      {!isClientFacing && (
        <Panel title="OneSource data extraction" className="mt-4">
          <div className="row items-center">
            <p className="col-6 no-margin">
              <span>
                To extract data from OneSource, a usage charge will be applied -
                please provide the market team WBS code below. It will not be
                possible to extract the OneSource data until this is provided.
                For further information, please refer to the{" "}
              </span>
              <a
                className="ap-link"
                href="https://pwcgbr.sharepoint.com/:w:/r/teams/GB-CoreBuild-CIRAppDevelopment/Shared Documents/CIRA Usage Charges.docx?d=we201144891a645c294540b7a145f9ca6&csf=1&web=1&e=bvb8kj"
                target="_blank"
                rel="noreferrer"
              >
                guidance linked here.
              </a>
            </p>
            <Input
              title="WBS Code"
              className="col-2"
              value={wbsCode}
              error={wbsCodeError}
              onChange={(value) => {
                value = String(value).trim().slice(0, 11);

                setWbsCode(value);
                setWbsCodeError(!String(value).match(/^\d{11}$|^$/));
              }}
            />
            <Button
              className="col-2"
              onClick={refresh_one_source}
              disabled={is_refreshing}
              loading={is_refreshing}
            >
              Refresh OneSource
            </Button>
          </div>
        </Panel>
      )}

      {/* {!isClientFacing && (
        <Panel title="Mean Accounting Date election" className="mt-4">
          <div className="panel-container">
            <p className="no-margin">
              Where companies in the group have a Mean Accounting Date election
              in place, select this button to apply the actual Accounting Period
              start and end dates in accordance with the election. Where
              companies have been linked to OneSource, the Accounting Period
              details should automatically reflect the impact of the election.
            </p>
            <Tooltip
              content="The mean accounting date election has already been applied for this PoA."
              disabled={!isMadElectionApplied}
            >
              <Button
                className="col-2"
                onClick={() => setMadElectionModalVisible(true)}
              >
                Apply Actual AP dates
              </Button>
            </Tooltip>
          </div>
        </Panel>
      )} */}

      {!isClientFacing && refreshSummary?.hasRunRefresh && (
        <OneSourceRefreshUpdatesPanel
          refreshSummary={refreshSummary}
          inputData={data}
        />
      )}

      <div
        id="portal"
        style={{ position: "fixed", left: 0, top: 0, zIndex: 9999 }}
      />
      {selectedPeriod && data?.length > 0 && (
        <EntityInputDataEditor
          inputData={data}
          editHandler={onCellEdited}
          capProps={capProps}
          changedMetricHighlights={changedMetricHighlights}
          navBarProps={{
            isPosting,
            isError,
            isAPsError,
            save,
            set_data,
            checkForAPsOverlapOrGap,
          }}
        />
      )}
    </>
  );
};

export default EntityInput;
